import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowRight, ExternalLink, Code, Play, CreditCard, Users, RefreshCw, BarChart3, ShoppingBag, Tag, Mail, Star } from "lucide-react";
import { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SEO } from "@/components/SEO";
import { getBreadcrumbSchema } from "@/lib/schema";

// The `id` for each flow is the canonical INTERNAL flow type (what the
// database and /create URL use). `aliases` lists legacy slugs that used to
// appear in deep-links (e.g. "waitlist", "pricing-table") — we still match
// them so old bookmarks keep working.
const flowTypes = [
  {
    id: "payment",
    aliases: [] as string[],
    icon: CreditCard,
    title: "Payment",
    description: "Validate willingness to pay for your product",
    isPopular: true,
    embedUrl: "https://willtheyconvert.com/embed/demo/payment",
    implementationCode: `<script src="${window.location.origin}/embed-button.js"></script>
<div
  data-wtc-payment-button
  data-flow-id="2ea08e51-fc36-4e52-9cde-27b2071b5146"
  data-text="Purchase Now"
  data-variant="gradient"
  data-size="md"
></div>`
  },
  {
    id: "signup",
    aliases: ["waitlist"],
    icon: Users,
    title: "Waitlist",
    description: "Collect email addresses for your product waitlist",
    isPopular: true,
    embedUrl: "https://willtheyconvert.com/embed/demo/waitlist",
    implementationCode: `<iframe
  src="${window.location.origin}/flow/990d30c4-c1ac-4794-b27f-ad4219f4f3e9"
  width="100%"
  height="600px"
  frameborder="0"
  allow="payment"
  style="border: none; display: block;"
></iframe>`,
    codeExample: `<!-- Hero Layout with Navbar -->
<iframe
  src="${window.location.origin}/flow/990d30c4-c1ac-4794-b27f-ad4219f4f3e9"
  width="100%"
  height="600px"
  frameborder="0"
  allow="payment"
></iframe>

<!-- Available Customizations (via WillTheyConvert Dashboard) -->
<!--
Content:
  - Title, subtitle, description, button text

Design:
  - Background color
  - Background image
  - Layout style (hero, centered, split, minimal, gradient)

Navbar (for hero layout):
  - Brand name
  - Navigation links
  - Social media icons
-->

Note: Customize in your WillTheyConvert dashboard under waitlist design settings`
  },
  {
    id: "subscription",
    aliases: [] as string[],
    icon: RefreshCw,
    title: "Subscription",
    description: "Test recurring payment model for your service",
    isPopular: false,
    embedUrl: "https://willtheyconvert.com/embed/demo/subscription",
    implementationCode: `<script src="${window.location.origin}/embed-button.js"></script>
<div data-wtc-payment-button data-flow-id="dc9054a0-f88a-4b19-940a-6d768c0fbb64" data-text="Subscribe Now" data-variant="gradient" data-size="md"></div>`
  },
  {
    id: "pricing_table",
    aliases: ["pricing-table", "pricingtable"],
    icon: BarChart3,
    title: "Pricing Table",
    description: "Compare multiple pricing tiers (up to 3)",
    isPopular: false,
    embedUrl: "https://willtheyconvert.com/embed/demo/pricing-table",
    implementationCode: `<iframe
  src="${window.location.origin}/flow/314738de-feae-461b-af10-cfbb0c69d179"
  width="100%"
  height="600px"
  frameborder="0"
  allow="payment"
></iframe>`
  },
  {
    id: "preorder",
    aliases: ["pre-order"],
    icon: ShoppingBag,
    title: "Pre-order",
    description: "Collect pre-orders for upcoming products",
    isPopular: false,
    embedUrl: "https://willtheyconvert.com/embed/demo/pre-order",
    implementationCode: `<script src="${window.location.origin}/embed-button.js"></script>
<div data-wtc-payment-button data-flow-id="cdee2d22-a76e-4065-9343-af47969fc93f" data-text="Pre-order Now" data-variant="gradient" data-size="md"></div>`
  },
  {
    id: "discount",
    aliases: [] as string[],
    icon: Tag,
    title: "Discount Test",
    description: "Test price sensitivity with discount offers",
    isPopular: false,
    embedUrl: "https://willtheyconvert.com/embed/demo/discount",
    implementationCode: `<script src="${window.location.origin}/embed-button.js"></script>
<div data-wtc-payment-button data-flow-id="f1f79f35-ca70-4c1c-84d0-334bc28bf979" data-text="Pay Now" data-variant="gradient" data-size="md"></div>`
  },
  {
    id: "referral",
    aliases: [] as string[],
    icon: Users,
    title: "Referral Program",
    description: "Test viral growth with referral incentives",
    isPopular: false,
    embedUrl: "https://willtheyconvert.com/embed/demo/referral",
    implementationCode: `<iframe
  src="${window.location.origin}/flow/7b1e1851-3077-43fc-a374-5f1a68f69b13"
  width="100%"
  height="600px"
  frameborder="0"
  allow="payment"
></iframe>`
  },
  {
    id: "newsletter",
    aliases: [] as string[],
    icon: Mail,
    title: "Newsletter",
    description: "Collect subscribers for your newsletter",
    isPopular: false,
    embedUrl: "https://willtheyconvert.com/embed/demo/newsletter",
    implementationCode: `<iframe
  src="${window.location.origin}/flow/0183a3ed-a28e-42d8-872b-72ecd5ad222d"
  width="100%"
  height="600px"
  frameborder="0"
  allow="payment"
></iframe>`
  }
];

const LiveDemo = ({ flow }) => {
  const containerRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check if this is a button-based demo by looking at the implementation code
  // Button demos use <script> and <div>, iframe demos use <iframe>
  const isButtonDemo = flow.implementationCode.includes('data-wtc-payment-button');

  useEffect(() => {
    if (containerRef.current && !isButtonDemo) {
      // Clear previous content
      containerRef.current.innerHTML = '';
      setIsLoading(true);

      // Add a small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        if (containerRef.current) {
          containerRef.current.innerHTML = flow.implementationCode;

          // Handle iframe loading
          const iframes = containerRef.current.querySelectorAll('iframe');

          if (iframes.length > 0) {
            let loadedCount = 0;
            iframes.forEach((iframe) => {
              // Ensure iframe has proper attributes
              iframe.style.border = 'none';
              iframe.style.width = '100%';
              iframe.style.minHeight = '600px';
              iframe.style.display = 'block';

              iframe.onload = () => {
                loadedCount++;
                if (loadedCount === iframes.length) {
                  setIsLoading(false);
                }
              };

              iframe.onerror = () => {
                loadedCount++;
                if (loadedCount === iframes.length) {
                  setIsLoading(false);
                }
              };
            });

            // Fallback timeout in case onload doesn't fire
            setTimeout(() => {
              setIsLoading(false);
            }, 3000);
          } else {
            // Handle script loading for non-iframe flows (button embeds)
            const script = document.createElement('script');
            script.src = `${window.location.origin}/embed-button.js`;
            script.async = true;
            script.onload = () => setIsLoading(false);
            script.onerror = () => setIsLoading(false);
            document.body.appendChild(script);

            return () => {
              if (document.body.contains(script)) {
                document.body.removeChild(script);
              }
            };
          }
        }
      }, 100);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [flow, isButtonDemo]);

  // For button demos, handle script loading separately
  useEffect(() => {
    if (isButtonDemo && containerRef.current) {
      setIsLoading(true);
      containerRef.current.innerHTML = flow.implementationCode;

      const script = document.createElement('script');
      script.src = `${window.location.origin}/embed-button.js`;
      script.async = true;
      script.onload = () => setIsLoading(false);
      script.onerror = () => setIsLoading(false);
      document.body.appendChild(script);

      return () => {
        if (document.body.contains(script)) {
          document.body.removeChild(script);
        }
      };
    }
  }, [flow, isButtonDemo]);

  // Button-based demo layout with centered, attention-grabbing design
  if (isButtonDemo) {
    return (
      <div className="bg-gradient-to-br from-indigo-50 via-white to-purple-50 rounded-xl relative overflow-hidden min-h-[600px]">
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/80 z-10 rounded-xl">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-2"></div>
              <p className="text-sm text-gray-600">Loading demo...</p>
            </div>
          </div>
        )}

        {/* Centered demo area */}
        <div className="flex flex-col items-center justify-center min-h-[600px] px-8 py-12 space-y-8">
          {/* Instruction header */}
          <div className="text-center space-y-3">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-100 rounded-full">
              <Play className="w-4 h-4 text-indigo-600" />
              <span className="text-sm font-medium text-indigo-700">Interactive Demo</span>
            </div>
            <h3 className="text-2xl font-semibold text-gray-900">
              Try it yourself
            </h3>
            <p className="text-base text-gray-600 max-w-md">
              Click the button below to experience the {flow.title.toLowerCase()} flow
            </p>
          </div>

          {/* Animated highlight container with pulsing effect */}
          <div className="relative">
            {/* Pulsing ring animation */}
            <div className="absolute inset-0 -m-6 bg-gradient-to-r from-indigo-400/30 to-purple-400/30 rounded-3xl animate-pulse"></div>

            {/* Actual demo button container */}
            <div className="relative z-10 bg-white rounded-2xl p-8 shadow-lg">
              <div
                ref={containerRef}
                className="flex items-center justify-center min-w-[200px]"
              />
            </div>
          </div>

          {/* Helper text with icon */}
          <div className="flex flex-col items-center gap-3 text-sm text-gray-500">
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              <span>This is a simulated {flow.title.toLowerCase()} - no real charges will be made</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Original iframe-based demo layout
  return (
    <div className={`bg-white rounded-xl shadow-lg relative overflow-hidden ${flow.id === 'pricing-table' ? 'p-8' : 'p-4'}`}>
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/80 z-10 rounded-xl">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-2"></div>
            <p className="text-sm text-gray-600">Loading demo...</p>
          </div>
        </div>
      )}
      <div
        ref={containerRef}
        className="w-full min-h-[600px] relative"
      />
    </div>
  );
};

const Demos = () => {
  const [activeFlow, setActiveFlow] = useState("payment");
  const [activeTab, setActiveTab] = useState("demo");

  // Get current flow
  const currentFlow = flowTypes.find(flow => flow.id === activeFlow);

  // Scroll to anchor on load and handle hash changes
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.slice(1);
      if (hash) {
        // Remove any potential special characters and convert to lowercase
        const cleanHash = hash.toLowerCase().replace(/[^a-z0-9]/g, '');
        const clean = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');
        // Find the matching flow — id, title, or alias
        const matchingFlow = flowTypes.find(flow =>
          clean(flow.id) === cleanHash ||
          clean(flow.title) === cleanHash ||
          (flow.aliases || []).some((a: string) => clean(a) === cleanHash)
        );
        if (matchingFlow) {
          setActiveFlow(matchingFlow.id);
          // Add a longer delay to ensure the content is fully rendered
          setTimeout(() => {
            const element = document.getElementById(matchingFlow.id);
            if (element) {
              element.scrollIntoView({ behavior: "smooth" });
            }
          }, 300);
        }
      }
    };

    // Handle initial load with a delay to ensure component is mounted
    const initialTimer = setTimeout(() => {
      handleHashChange();
    }, 100);

    // Add event listener for hash changes
    window.addEventListener('hashchange', handleHashChange);

    // Cleanup
    return () => {
      clearTimeout(initialTimer);
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-muted/10 to-background/90">
      <SEO
        title="Flow Demos - Interactive Validation Test Examples | WillTheyConvert"
        description="Explore interactive demos of all validation flows. See payment tests, waitlists, pricing tables, and more in action before creating your own."
        keywords="validation demos, fake door testing examples, payment flow demo, waitlist demo, pricing table demo"
        canonicalUrl="/demos"
        jsonLd={getBreadcrumbSchema([
          { name: "Home", url: "/" },
          { name: "Demos", url: "/demos" },
        ])}
      />

      {/* Header */}
      <div className="py-16 bg-gradient-to-b from-muted/30 to-background/90">
        <div className="container max-w-6xl mx-auto px-4">
          <div className="text-center">
            <h1 className="text-4xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-indigo-500 to-blue-600">
              Flow Demos
            </h1>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Explore interactive demos of all our validation flows. Each demo shows you exactly how your customers will experience the test.
            </p>
          </div>
        </div>
      </div>

      {/* Flow Selection */}
      <div className="sticky top-0 z-50 bg-white/80 backdrop-blur-sm border-b border-gray-200/50 py-4">
        <div className="container max-w-6xl mx-auto px-4">
          <div className="flex flex-wrap gap-2 md:gap-3 justify-center pb-2">
            {flowTypes.map((flow) => {
              const Icon = flow.icon;
              return (
                <button
                  key={flow.id}
                  onClick={() => {
                    setActiveFlow(flow.id);
                    window.location.hash = flow.id;
                  }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all whitespace-nowrap text-sm ${
                    activeFlow === flow.id
                      ? "bg-gradient-to-r from-indigo-500 to-blue-600 text-white shadow-md"
                      : "bg-white shadow-sm hover:shadow-md"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{flow.title}</span>
                  {flow.isPopular && (
                    <Star className="w-3 h-3 fill-[#F6B827] text-[#F6B827]" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Current Flow Content */}
      <div className="py-16">
        <div className="container max-w-6xl mx-auto px-4">
          {currentFlow && (
            <div className="space-y-8">
              {/* Flow Header */}
              <div id={currentFlow.id} className="text-center scroll-mt-32">
                <div className="flex items-center justify-center gap-3 mb-4">
                  {(() => {
                    const Icon = currentFlow.icon;
                    return <Icon className="w-10 h-10 text-indigo-600" />;
                  })()}
                  <h2 className="text-3xl font-bold">{currentFlow.title}</h2>
                  {currentFlow.isPopular && (
                    <span className="bg-[#F6B827] text-white text-sm px-3 py-1 rounded-full flex items-center gap-1">
                      <Star className="w-4 h-4 fill-white" /> Popular
                    </span>
                  )}
                </div>
                <p className="text-muted-foreground text-lg mb-8">
                  {currentFlow.description}
                </p>
                <Button
                  asChild
                  className="gap-2 bg-gradient-to-r from-indigo-500 to-blue-600 hover:from-indigo-600 hover:to-blue-700"
                >
                  <Link to={`/create?type=${currentFlow.id}`}>
                    Create Your Own <ArrowRight className="w-4 h-4" />
                  </Link>
                </Button>
              </div>

              {/* Demo and Code */}
              <div className={`${currentFlow?.id === 'pricing-table' ? 'max-w-7xl' : 'max-w-5xl'} mx-auto`}>
                <Tabs defaultValue="demo" className="w-full" value={activeTab} onValueChange={setActiveTab}>
                  <TabsList className="mb-4">
                    <TabsTrigger value="demo" className="flex items-center gap-2">
                      <Play className="w-4 h-4" />
                      Live Demo
                    </TabsTrigger>
                    <TabsTrigger value="code" className="flex items-center gap-2">
                      <Code className="w-4 h-4" />
                      Implementation
                    </TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="demo" className="min-h-[700px]">
                    <LiveDemo flow={currentFlow} />
                  </TabsContent>
                  
                  <TabsContent value="code">
                    <div className="bg-gray-950 rounded-xl shadow-lg p-4">
                      <pre className="text-gray-100 overflow-x-auto">
                        <code>{currentFlow.codeExample || currentFlow.implementationCode}</code>
                      </pre>
                    </div>

                    {/* Show customization options for waitlist */}
                    {currentFlow.id === 'waitlist' && (
                      <div className="mt-4 p-6 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg border border-indigo-100">
                        <h3 className="font-semibold text-indigo-900 mb-4 flex items-center gap-2">
                          <Code className="w-5 h-5" />
                          Available Customization Options
                        </h3>
                        <div className="grid md:grid-cols-3 gap-6">
                          <div>
                            <h4 className="font-medium text-indigo-800 mb-2">Content (4 options)</h4>
                            <ul className="text-sm text-indigo-700 space-y-1">
                              <li>• Title</li>
                              <li>• Subtitle</li>
                              <li>• Description</li>
                              <li>• Button text</li>
                            </ul>
                          </div>
                          <div>
                            <h4 className="font-medium text-purple-800 mb-2">Design (3 options)</h4>
                            <ul className="text-sm text-purple-700 space-y-1">
                              <li>• Background color</li>
                              <li>• Background image</li>
                              <li>• Layout style (hero, centered, split, minimal, gradient)</li>
                            </ul>
                          </div>
                          <div>
                            <h4 className="font-medium text-pink-800 mb-2">Navbar (4 options)</h4>
                            <ul className="text-sm text-pink-700 space-y-1">
                              <li>• Brand name</li>
                              <li>• Navigation links</li>
                              <li>• Social media icons</li>
                              <li>• Show/hide navbar</li>
                            </ul>
                          </div>
                        </div>
                        <p className="text-xs text-indigo-600 mt-4">
                          💡 All customizations are configured in your WillTheyConvert dashboard under waitlist design settings
                        </p>
                      </div>
                    )}

                    <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-100">
                      <h3 className="font-medium text-blue-900 mb-2">How to implement:</h3>
                      <ol className="list-decimal list-inside text-sm text-blue-800 space-y-2">
                        <li>Copy the code above</li>
                        <li>Paste it into your website's HTML where you want the flow to appear</li>
                        <li>Replace the flow-id with your own flow ID from the dashboard</li>
                        <li>Customize the appearance using the data attributes</li>
                      </ol>
                    </div>
                  </TabsContent>
                </Tabs>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Demos; 